import { useState, useEffect, useRef } from "react";
import { Shield, Camera, ChevronDown, Lock, Zap, Users } from "lucide-react";

export default function MockupShield() {
  const [scrollY, setScrollY] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleScroll = () => setScrollY(container.scrollTop);
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div ref={containerRef} className="h-screen overflow-y-auto bg-[#0F1729] text-white" style={{ scrollBehavior: "smooth" }}>
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-5 py-4" style={{ backgroundColor: scrollY > 50 ? "rgba(15,23,41,0.95)" : "transparent", backdropFilter: scrollY > 50 ? "blur(12px)" : "none", transition: "all 0.3s" }}>
        <img src="/toolio-logo-new.png" alt="TOOLIO" className="h-10" />
        <button className="bg-yellow-500 text-black text-sm font-bold px-4 py-2 rounded-sm tracking-wide">GET STARTED</button>
      </nav>

      {/* Hero */}
      <section className="relative min-h-screen flex flex-col items-center justify-center px-6 pt-20 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F1729] via-[#0F1729] to-[#0a1020]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-yellow-500/5 rounded-full blur-3xl" style={{ transform: `translate(-50%, ${scrollY * -0.3}px)` }} />
        
        <div className="relative z-10 text-center">
          <img src="/toolio-logo-new.png" alt="TOOLIO" className="h-24 mx-auto mb-8" style={{ transform: `translateY(${scrollY * -0.1}px)` }} />
          
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight leading-[1.1] mb-6">
            PROTECT YOUR<br/>TOOLS.<br/>
            <span className="text-yellow-500">STOP THE<br/>THIEVES.</span>
          </h1>
          
          <p className="text-gray-400 text-base max-w-xs mx-auto mb-10 leading-relaxed">
            The smart tool identification system trusted by tradespeople and police across the UK.
          </p>

          <div className="flex gap-3 justify-center mb-12">
            <button className="bg-yellow-500 text-black font-bold px-6 py-3.5 rounded-sm text-sm tracking-wider">GET STARTED</button>
            <button className="border border-yellow-500/50 text-yellow-400 font-medium px-6 py-3.5 rounded-sm text-sm tracking-wider">CHECK TOOL</button>
          </div>
        </div>

        <ChevronDown className="absolute bottom-8 left-1/2 -translate-x-1/2 w-6 h-6 text-yellow-500/60 animate-bounce" />
      </section>

      {/* Dashboard Preview */}
      <section className="relative py-16 px-6">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1020] to-[#0F1729]" />
        <div className="relative z-10">
          <p className="text-yellow-500 text-xs font-bold tracking-[0.3em] uppercase mb-3 text-center">Your Dashboard</p>
          <h2 className="text-2xl font-bold text-center mb-8">Everything at a glance</h2>
          
          <div className="relative mx-auto max-w-sm" style={{ transform: `translateY(${Math.max(0, (scrollY - 400) * -0.05)}px)` }}>
            <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-yellow-500/5">
              <img src="/dashboard-preview.png" alt="TOOLIO Dashboard" className="w-full" />
            </div>
            <div className="absolute -bottom-4 left-4 right-4 h-8 bg-yellow-500/10 rounded-2xl blur-xl" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6">
        <p className="text-yellow-500 text-xs font-bold tracking-[0.3em] uppercase mb-3 text-center">How It Works</p>
        <h2 className="text-2xl font-bold text-center mb-12">Three steps to protection</h2>

        {[
          { icon: <Camera className="w-6 h-6" />, title: "Scan Your Label", desc: "AI-powered OCR reads your tool's label instantly — manufacturer, model, serial number, all captured in seconds." },
          { icon: <Lock className="w-6 h-6" />, title: "Register & Protect", desc: "Each tool gets a unique TOOLIO ID. Mark it as yours with verified ownership that nobody can dispute." },
          { icon: <Shield className="w-6 h-6" />, title: "Recover If Stolen", desc: "Report stolen tools instantly. Police and buyers can check any tool's status before purchase." }
        ].map((item, i) => (
          <div key={i} className="flex gap-4 mb-8 last:mb-0">
            <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center text-yellow-500">
              {item.icon}
            </div>
            <div>
              <h3 className="font-bold text-base mb-1">{item.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
            </div>
          </div>
        ))}
      </section>

      {/* Stats */}
      <section className="py-16 px-6 bg-[#0a1020]">
        <div className="grid grid-cols-3 gap-4 text-center">
          {[
            { num: "10K+", label: "Tools Protected" },
            { num: "50+", label: "Police Forces" },
            { num: "< 60s", label: "To Register" }
          ].map((s, i) => (
            <div key={i}>
              <div className="text-2xl font-black text-yellow-500">{s.num}</div>
              <div className="text-xs text-gray-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Trust */}
      <section className="py-16 px-6 text-center">
        <h2 className="text-2xl font-bold mb-3">Trusted by professionals</h2>
        <p className="text-gray-400 text-sm mb-10 max-w-xs mx-auto">Join thousands of tradespeople who protect their livelihood with TOOLIO.</p>
        <button className="bg-yellow-500 text-black font-bold px-8 py-4 rounded-sm text-sm tracking-wider w-full max-w-xs">GET STARTED FREE</button>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/5 text-center">
        <img src="/toolio-logo-new.png" alt="TOOLIO" className="h-8 mx-auto mb-3" />
        <p className="text-xs text-gray-600">tool-io.uk</p>
      </footer>
    </div>
  );
}
